/**
 * SCWT-PM-V2 Phase 5 — PM Landlords tab (replaces Phase 3 placeholder).
 * Lists agency landlords + Invite Landlord modal.
 */
import { useEffect, useState } from "react";
import { Users, UserPlus, Loader2, Mail, Home } from "lucide-react";
import api from "@/lib/api";

export default function PmLandlordsTab() {
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    property_id: "", landlord_email: "", landlord_name: "",
    relationship_type: "owner", optional_pm_message: "",
    authorisation_confirmed: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = async () => {
    try {
      const [l, inv, p] = await Promise.all([
        api.get("/property-manager/landlords"),
        api.get("/property-manager/landlord-invitations"),
        api.get("/property-manager/properties"),
      ]);
      setItems(l.data.items || []);
      setInvites(inv.data.items || []);
      setProperties(p.data.items || p.data.properties || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("/property-manager/landlord-invitations", form);
      setInviteOpen(false);
      setForm({ property_id: "", landlord_email: "", landlord_name: "",
                relationship_type: "owner", optional_pm_message: "",
                authorisation_confirmed: false });
      reload();
    } catch (e) { setError(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;

  return (
    <div data-testid="pm-landlords-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Landlords</h2>
          <p className="text-xs text-slate-500">Invite property owners to the Aware Landlord Portal.</p>
        </div>
        <button data-testid="pm-landlords-invite-btn"
                 onClick={() => setInviteOpen(true)}
                 className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 text-white font-semibold rounded-full px-5 py-2 text-sm">
          <UserPlus className="w-4 h-4" /> Invite landlord
        </button>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold text-slate-700 inline-flex items-center gap-2"><Users className="w-4 h-4" /> Linked landlords</div>
        {items.length === 0 ? (
          <div data-testid="pm-landlords-empty" className="p-8 text-center text-sm text-slate-500">No landlords have accepted an invitation yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((l) => (
              <li key={l.landlord_id} data-testid={`pm-landlord-${l.safe_reference}`} className="px-6 py-4 flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{l.display_name || l.email}</div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {l.email}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{l.properties.length} property{l.properties.length===1?"":"ies"}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold text-slate-700">Invitations</div>
        {invites.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No invitations sent.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invites.map((i) => (
              <li key={i.id} data-testid={`pm-landlord-invite-${i.reference}`} className="px-6 py-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{i.landlord_name}</div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {i.landlord_email}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{i.reference} · expires {i.expires_at?.slice(0, 10)}</div>
                </div>
                <span className="inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-1 uppercase tracking-wide bg-slate-100 text-slate-700">{i.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {inviteOpen && (
        <div data-testid="pm-landlord-invite-dialog" className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900">Invite a landlord</h3>
            <div className="mt-4 space-y-3">
              <select data-testid="pm-landlord-invite-property"
                       value={form.property_id}
                       onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}
                       className={inp}>
                <option value="">Property…</option>
                {properties.map((p) => {
                  const prop = p.property || p;
                  return <option key={prop.id} value={prop.id}>{prop.address?.street || prop.street}, {prop.address?.suburb || prop.suburb}</option>;
                })}
              </select>
              <input data-testid="pm-landlord-invite-name" placeholder="Landlord name"
                      value={form.landlord_name}
                      onChange={(e) => setForm((f) => ({ ...f, landlord_name: e.target.value }))}
                      className={inp} />
              <input data-testid="pm-landlord-invite-email" type="email" placeholder="Landlord email"
                      value={form.landlord_email}
                      onChange={(e) => setForm((f) => ({ ...f, landlord_email: e.target.value }))}
                      className={inp} />
              <select data-testid="pm-landlord-invite-relationship"
                       value={form.relationship_type}
                       onChange={(e) => setForm((f) => ({ ...f, relationship_type: e.target.value }))}
                       className={inp}>
                <option value="owner">Owner</option>
                <option value="co_owner">Co-owner</option>
                <option value="authorised_representative">Authorised representative</option>
              </select>
              <textarea data-testid="pm-landlord-invite-message"
                        placeholder="Optional message"
                        value={form.optional_pm_message}
                        rows={2} maxLength={500}
                        onChange={(e) => setForm((f) => ({ ...f, optional_pm_message: e.target.value }))}
                        className={inp} />
              <label className="flex gap-2 items-start text-xs text-slate-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <input data-testid="pm-landlord-invite-auth"
                        type="checkbox"
                        checked={form.authorisation_confirmed}
                        onChange={(e) => setForm((f) => ({ ...f, authorisation_confirmed: e.target.checked }))} />
                I confirm this agency is authorised to invite this person to view reports for this property.
              </label>
            </div>
            {error && <div data-testid="pm-landlord-invite-error" className="mt-3 text-sm text-rose-700">{error}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setInviteOpen(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button data-testid="pm-landlord-invite-submit"
                       disabled={busy || !form.property_id || !form.landlord_email || !form.landlord_name || !form.authorisation_confirmed}
                       onClick={submit}
                       className="bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white text-sm font-semibold rounded-full px-4 py-2 inline-flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Send invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";
